import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import type { ServiceVariableTemplate, ServiceVariableTemplateFormData } from '../types';

interface ServiceVariableTemplateState {
  templates: ServiceVariableTemplate[];
  loading: boolean;
  error: string | null;

  // Actions
  fetchTemplates: (serviceId: string) => Promise<void>;
  addTemplate: (serviceId: string, data: ServiceVariableTemplateFormData) => Promise<ServiceVariableTemplate>;
  updateTemplate: (id: string, data: Partial<ServiceVariableTemplateFormData>) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;
  getTemplatesByServiceId: (serviceId: string) => ServiceVariableTemplate[];
  clearTemplates: () => void;
  clearTemplatesForService: (serviceId: string) => void;
}

export const useServiceVariableTemplateStore = create<ServiceVariableTemplateState>((set, get) => ({
  templates: [],
  loading: false,
  error: null,

  fetchTemplates: async (serviceId: string) => {
    console.log('🔍 DEBUG STORE: fetchTemplates chiamato per serviceId:', serviceId);
    set({ loading: true, error: null });

    try {
      console.log('🔍 DEBUG STORE: Eseguendo query al database...');
      const { data, error } = await supabase
        .from('service_variable_templates')
        .select('*')
        .eq('service_id', serviceId)
        .order('sort_order', { ascending: true });

      console.log('🔍 DEBUG STORE: Risultato query:', { data, error });

      if (error) throw error;

      console.log('✅ DEBUG STORE: Template caricati dal database:', data?.length || 0);
      if (data && data.length > 0) {
        console.log('✅ DEBUG STORE: Template trovati:', data);
      }

      // FIX: Accumula i template invece di sovrascrivere
      // Rimuove i template del servizio corrente e aggiunge quelli nuovi
      // In questo modo i template di altri servizi restano nello store
      set(state => ({
        templates: [
          ...state.templates.filter(t => t.service_id !== serviceId),
          ...(data || [])
        ],
        loading: false
      }));

      console.log('✅ DEBUG STORE: Template aggiornati nello store (mantenendo altri servizi)');
    } catch (error: any) {
      console.error('❌ DEBUG STORE: Error fetching service variable templates:', error);
      set({
        error: friendlyDbError(error) || 'Errore nel caricamento delle variabili template',
        loading: false
      });
    }
  },

  addTemplate: async (serviceId: string, data: ServiceVariableTemplateFormData) => {
    set({ loading: true, error: null });
    
    try {
      // Ottieni l'organizzazione dell'utente corrente
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (profileError) throw profileError;

      // Calcola il prossimo sort_order
      const existingTemplates = get().templates.filter(t => t.service_id === serviceId);
      const nextSortOrder = existingTemplates.length > 0 
        ? Math.max(...existingTemplates.map(t => t.sort_order)) + 1 
        : 0;

      // is_internal_cost esiste solo su deal_variables, NON su service_variable_templates:
      // PostgREST rifiuta l'intera insert se viene inviato (segnalazione Finup 12 giu 2026)
      const { is_internal_cost: _omit, ...cleanData } = data as Record<string, unknown>;
      const templateData = {
        ...cleanData,
        service_id: serviceId,
        organization_id: userProfile.organization_id,
        sort_order: data.sort_order ?? nextSortOrder,
      };

      const { data: newTemplate, error } = await supabase
        .from('service_variable_templates')
        .insert([templateData])
        .select()
        .single();

      if (error) throw error;

      set(state => ({
        templates: [...state.templates, newTemplate],
        loading: false
      }));

      return newTemplate;
    } catch (error: any) {
      console.error('Error adding service variable template:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nell\'aggiunta della variabile template',
        loading: false 
      });
      throw error;
    }
  },

  updateTemplate: async (id: string, data: Partial<ServiceVariableTemplateFormData>) => {
    set({ loading: true, error: null });
    
    try {
      // Stessa difesa di addTemplate: is_internal_cost non è una colonna di questa tabella
      const { is_internal_cost: _omit, ...cleanData } = data as Record<string, unknown>;
      const { error } = await supabase
        .from('service_variable_templates')
        .update(cleanData)
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        templates: state.templates.map(template =>
          template.id === id ? { ...template, ...data } : template
        ),
        loading: false
      }));
    } catch (error: any) {
      console.error('Error updating service variable template:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nell\'aggiornamento della variabile template',
        loading: false 
      });
      throw error;
    }
  },

  deleteTemplate: async (id: string) => {
    set({ loading: true, error: null });
    
    try {
      const { error } = await supabase
        .from('service_variable_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        templates: state.templates.filter(template => template.id !== id),
        loading: false
      }));
    } catch (error: any) {
      console.error('Error deleting service variable template:', error);
      set({ 
        error: friendlyDbError(error) || 'Errore nell\'eliminazione della variabile template',
        loading: false 
      });
      throw error;
    }
  },

  getTemplatesByServiceId: (serviceId: string) => {
    const allTemplates = get().templates;
    const filtered = allTemplates.filter(template => template.service_id === serviceId);
    console.log('🔍 DEBUG STORE: getTemplatesByServiceId per', serviceId, '- Tutti i template:', allTemplates.length, '- Filtrati:', filtered.length);
    return filtered;
  },

  clearTemplates: () => {
    set({ templates: [], error: null });
  },

  clearTemplatesForService: (serviceId: string) => {
    console.log('🧹 DEBUG STORE: clearTemplatesForService chiamato per serviceId:', serviceId);
    set(state => ({
      templates: state.templates.filter(template => template.service_id !== serviceId)
    }));
    console.log('✅ DEBUG STORE: Template del servizio rimossi dallo store');
  },
}));